import React, { useEffect, useRef } from "react";

import useSound from "use-sound";
import pluralize from "pluralize";

import { useNavigate } from "react-router-dom";
import { useGame } from "../../contexts/GameProvider";

import {
  Player,
  BigPlayButton,
  ControlBar,
  FullscreenToggle,
  VolumeMenuButton,
} from "video-react";
import RedFlag from "../Icon/RedFlag/RedFlag";
import Check from "../Icon/Check/Check";

import "../../../node_modules/video-react/dist/video-react.css";
import "./Game.css";

import clickDownSfx from "../../sfx/bloop-1.mp3";
import clickOnSfx from "../../sfx/bloop-2.mp3";
import clickOffSfx from "../../sfx/bloop-4.mp3";
import {
  ForwardIcon,
  PauseIcon,
  PlayIcon,
  RewindIcon,
} from "../Icon/Media/Media";

export default function Game() {
  const player = useRef(null);

  const {
    gameState,
    gameDispatch,
    setActiveSubtitleIndex,
    flagActiveSubtitleIndex,
    submitAnswers,
  } = useGame();

  const navigate = useNavigate();

  const [clickDown] = useSound(clickDownSfx, { volume: 0.75 });
  const [clickOn] = useSound(clickOnSfx, { volume: 0.75 });
  const [clickOff] = useSound(clickOffSfx, { volume: 0.75 });

  const flagsUsed = gameState.subtitles.filter((el) => el.flagged).length,
    totalFlags = gameState.subtitles.filter((el) => el.redFlag).length,
    isCurrentSubtitleFlagged =
      gameState.subtitles[gameState.activeSubtitleIndex].flagged,
    isCurrentSubtitleDisabled =
      gameState.subtitles[gameState.activeSubtitleIndex].disabled;

  /**
   * Read subtitles through a ref so the effect below can stay mounted for the
   * lifetime of the player — re-running it on every flag would stack a new
   * state subscription on top of the old one.
   */
  const subtitlesRef = useRef(gameState.subtitles);
  subtitlesRef.current = gameState.subtitles;

  useEffect(() => {
    const updateSubtitles = () => {
      const reversed = [].concat(subtitlesRef.current).reverse();

      const newPlayerState = player.current.getState();

      let index = reversed.findIndex((element) => {
        return newPlayerState.player.currentTime >= element.startTime;
      });

      gameDispatch(setActiveSubtitleIndex(reversed.length - index - 1));
    };

    const updateInterval = setInterval(() => updateSubtitles(), 500);

    player.current.subscribeToStateChange(() => updateSubtitles());

    return () => clearInterval(updateInterval);
  }, [gameDispatch, setActiveSubtitleIndex]);

  return (
    <>
      <div className="game">
        <div className="task-header">
          <h1 className="task-title">The Hedge-Fund Hoaxer</h1>
        </div>
        <div className="task-bar">
          <div className="task-description">
            <p>
              There are {totalFlags} Red Flags to find. Watch the video and use
              the <strong>Red Flag</strong> button to mark points in the video
              when you notice fraud.
            </p>
            <p>
              You can pause and rewind the video, and remove flags you’ve
              already placed.
            </p>
          </div>
          <div className="subtitles-wrapper">
            <div
              className="subtitles"
              style={{
                transform:
                  "translateY(-" + gameState.activeSubtitleIndex * 4 + "em)",
              }}
            >
              {gameState.subtitles.map((el, index) => (
                <div
                  className={[
                    "subtitle-line",
                    index === gameState.activeSubtitleIndex && "active-line",
                    el.flagged && "flagged-line",
                  ].join(" ")}
                  onClick={() => {
                    player.current.seek(el.startTime);
                    player.current.play();
                  }}
                  key={index}
                >
                  {el.flagged && <RedFlag />}
                  {el.text}
                </div>
              ))}
            </div>
          </div>
          <div className="controls">
            <button
              className="btn btn-media-control"
              id="rewindBtn"
              onMouseDown={clickDown}
              onMouseUp={() => {
                clickOn();
                player.current.replay(10);
              }}
            >
              <RewindIcon style={{ fill: "white" }} />
            </button>
            <button
              className="btn btn-media-control"
              id="playBtn"
              onMouseDown={clickDown}
              onMouseUp={() => {
                clickOn();
                player.current && player.current.getState().player.paused
                  ? player.current.play()
                  : player.current.pause();
              }}
            >
              {player.current && player.current.getState().player.paused ? (
                <PlayIcon style={{ fill: "white" }} />
              ) : (
                <PauseIcon style={{ fill: "white" }} />
              )}
            </button>
            <button
              className="btn btn-media-control"
              id="forwardBtn"
              onMouseDown={clickDown}
              onMouseUp={() => {
                clickOn();
                player.current.forward(10);
              }}
            >
              <ForwardIcon style={{ fill: "white" }} />
            </button>
            <button
              id="redFlagBtn"
              className="btn"
              onMouseDown={clickDown}
              onMouseUp={() => {
                if (
                  gameState.subtitles[gameState.activeSubtitleIndex].disabled
                ) {
                  return false;
                }

                /**
                 * If we already have 3+ flags, and the current subtitle isn't flagged
                 * already, return the current gameState without making changes
                 */
                if (
                  gameState.subtitles.filter((el) => el.flagged).length >=
                    gameState.subtitles.filter((el) => el.redFlag).length &&
                  !gameState.subtitles[gameState.activeSubtitleIndex].flagged
                ) {
                  return false;
                }

                gameState.subtitles[gameState.activeSubtitleIndex].flagged
                  ? clickOff()
                  : clickOn();

                gameDispatch(flagActiveSubtitleIndex());
              }}
              disabled={
                (flagsUsed >=
                  gameState.subtitles.filter((el) => el.redFlag).length &&
                  !isCurrentSubtitleFlagged) ||
                isCurrentSubtitleDisabled
              }
            >
              <RedFlag
                style={{
                  fill: "white",
                }}
                remove={isCurrentSubtitleFlagged}
              />
              {isCurrentSubtitleFlagged ? "Remove Flag" : "Red Flag"}
            </button>
            <button
              id="completeBtn"
              className="btn btn-confirm"
              disabled={
                flagsUsed <
                gameState.subtitles.filter((el) => el.redFlag).length
              }
              onMouseDown={clickDown}
              onMouseUp={() => {
                submitAnswers({
                  name: gameState.name,
                  answers: gameState.subtitles.map((el) => el.flagged),
                });

                clickOn();

                navigate("/complete");
              }}
            >
              <Check
                style={{
                  fill: "white",
                }}
              />
              Complete
            </button>
          </div>
          <div className="flags-status">
            <div className="flags-remaining">
              {[...Array(totalFlags)].map((e, i) => (
                <RedFlag
                  style={{
                    opacity: i < totalFlags - flagsUsed ? 1 : 0.2,
                  }}
                  size="1.5em"
                />
              ))}
              <span className="flags-remaining-text">
                {pluralize("Red Flag", totalFlags - flagsUsed, true)} remaining
              </span>
            </div>
          </div>
        </div>
        <div className="video-wrapper">
          <Player
            ref={player}
            playsInline
            poster="/assets/poster.png"
            src="/assets/part1.mp4"
          >
            <BigPlayButton position="center" />
            <ControlBar autoHide={false}>
              <VolumeMenuButton order={7} vertical />
              <FullscreenToggle disabled />
            </ControlBar>
          </Player>
        </div>
      </div>
    </>
  );
}
